import AddIcon from "@mui/icons-material/Add";
import RestoreIcon from '@mui/icons-material/Restore';
import TranslateIcon from '@mui/icons-material/Translate';
import { Box, Button, Checkbox, CircularProgress, Container, Fab, FormControlLabel, Grid, InputAdornment, Switch, TextField, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import ComboBox from "../components/ComboBox";
import CustomDialog from "../components/CustomDialog";
import CustomToolTip from "../components/CustomToolTip";
import ImageBox from "../components/ImageBox";
import LanguagesCheckbox from "../components/LanguagesCheckbox";
import TranslationTextFields from "../components/TranslationTextFields";
import useSnackbar from "../components/snackbar/useSnackBar";
import DataTable from "../data/DataTable";
import { useApi } from "../hooks/useApi";
import { useLanguage } from "../hooks/useLanguage";
import { API_URL } from "../utils/lib";
import { defaultMaterialData, mapTypes } from "./MapUtil";
import MaterialImageCard from "./MaterialImageCard";
import MaterialPreview from "./MaterialPreview";
import TextFieldElement from "./helper/TextFieldElement";

const textFieldStyle = { width: '90%' };


export default function EditMaterial({ userInfo, version: productVersion, pid: productPropId, id: modelPropId, onClose, fetchThroughProduct, viewStatic = false }) {
  // retrieve the id and pid either through params or props
  const { id: modelId } = useParams();
  const id = modelPropId || modelId;
  const pid = productPropId;
  const [searchParams, setSearchParams] = useSearchParams();
  const version = productVersion ?? searchParams.get("version");
  const versionSearchParam = `${version != null ? `version=${version}` : ''}`

  const location = useLocation();
  const { fromTab } = location.state || {};

  // State that holds all material-data.
  const [data, setData] = useState(
    {
      name: '',
      localization: {},
      publicFlag: false,
      object: { id: undefined, name: '' },
      ...defaultMaterialData
    });
  // State for initial data, that should not change later on.
  // Should be filled with initial data from the server.
  const [initialData, setInitialData] = useState({ name: '', ...defaultMaterialData });
  // state used for triggering a change in the initial-data for the TextFieldElement
  const [triggerDataChange, setTriggerDataChange] = useState(1);
  // materials for react three fiber
  const [materials, setMaterials] = useState({});
  const [materialMaps, setMaterialMaps] = useState([]);
  const [mapsToRemove, setMapsToRemove] = useState([]);
  const [previewPicture, setPreviewPicture] = useState({});
  const [deletePreviewPicture, setDeletePreviewPicture] = useState(false);
  const nextId = useRef(-1);
  const [isLoading, setIsLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [openMapFileChooser, setOpenMapFileChooser] = useState(false);
  const [openPreviewPictureChooser, setOpenPreviewPictureChooser] = useState(false);

  const isStatic = version != null || viewStatic || (id !== 'new' && !userInfo?.isAdmin && data.ownerId !== userInfo?.ownerId);

  let fetchPrefix = '';
  if (fetchThroughProduct) {
    fetchPrefix += `/product/${pid}`;
  }

  // ref that references all translations
  const translationsRef = useRef();
  // if translations should be shown or not
  const [showTranslations, setShowTranslations] = useState(false);

  const navigate = useNavigate();

  const { locale } = useLanguage();
  const intl = useIntl();
  const api = useApi();
  // if all languages should be fetched
  const [allLanguages, setAllLanguages] = useState(false);

  // snackbar for user-information
  const { openSnackBar } = useSnackbar();

  const normalForm = {
    length: {
      type: "number",
      label: intl.formatMessage({ id: "EditMaterial.length" }),
      endAdornment: <InputAdornment positon="end">mm</InputAdornment>,
      inputProps: { min: 0, step: 1 }
    },
    width: {
      type: "number",
      label: intl.formatMessage({ id: "EditMaterial.width" }),
      endAdornment: <InputAdornment position="end" >mm</InputAdornment>,
      inputProps: { min: 0, step: 1 }
    }
  }

  const specialForm = {
    color: { type: "color", label: "color" },
    emissiveColor: { type: "color", label: "emissiveColor" },
    emissiveIntensity: {
      type: "number",
      inputProps: { step: 0.01 },
      label: "emissiveIntensity",
    },
    roughness: {
      type: "number",
      inputProps: { min: 0.0, max: 1.0, step: 0.01 },
      label: "roughness",
    },
    metalness: {
      type: "number",
      inputProps: { min: 0.0, max: 1.0, step: 0.01 },
      label: "metalness",
    },
    reflectivity: {
      type: "number",
      inputProps: { min: 0.0, max: 1.0, step: 0.01 },
      label: "reflectivity",
    },
    clearcoat: {
      type: "number",
      inputProps: { min: 0.0, max: 1.0, step: 0.01 },
      label: "clearcoat",
    },
    clearcoatRoughness: {
      type: "number",
      inputProps: { min: 0.0, max: 1.0, step: 0.01 },
      label: "clearcoatRoughness",
    },
    sheen: {
      type: "number",
      inputProps: { min: 0.0, max: 1.0, step: 0.01 },
      label: "sheen",
    },
    sheenRoughness: {
      type: "number",
      inputProps: { min: 0.0, max: 1.0, step: 0.01 },
      label: "sheenRoughness",
    },
    transmission: {
      type: "number",
      inputProps: { min: 0.0, max: 1.0, step: 0.01 },
      label: "transmission",
    },
    thickness: {
      type: "number",
      inputProps: { step: 0.01 },
      label: "thickness",
    },
    opacity: {
      type: "number",
      inputProps: { min: 0.0, max: 1.0, step: 0.01 },
      label: "opacity",
    },
    flatShading: {
      label: 'flatShading',
      type: 'checkbox',
      select: true,
      children: [
        {
          value: false,
          name: intl.formatMessage({ id: "EditMaterial.false" })
        },
        {
          value: true,
          name: intl.formatMessage({ id: "EditMaterial.true" })
        }
      ]
    },
    depthTest: {
      label: 'depthTest',
      type: 'checkbox',
      select: true,
      children: [
        {
          value: false,
          name: intl.formatMessage({ id: "EditMaterial.false" })
        },
        {
          value: true,
          name: intl.formatMessage({ id: "EditMaterial.true" })
        }
      ]
    },
    depthWrite: {
      label: 'depthWrite',
      type: 'checkbox',
      select: true,
      children: [
        {
          value: false,
          name: intl.formatMessage({ id: "EditMaterial.false" })
        },
        {
          value: true,
          name: intl.formatMessage({ id: "EditMaterial.true" })
        }
      ]
    },
    bumpScale: {
      type: "number",
      inputProps: { min: 0.0, max: 1.0, step: 0.01 },
      label: "bumpScale",
    },
    normalScaleX: {
      type: "number",
      inputProps: { min: 0.0, max: 1.0, step: 0.01 },
      label: "normalScaleX",
    },
    normalScaleY: {
      type: "number",
      inputProps: { min: 0.0, max: 1.0, step: 0.01 },
      label: "normalScaleY",
    },
    aoMapIntensity: {
      type: "number",
      inputProps: { min: 0.0, max: 1.0, step: 0.01 },
      label: "aoMapIntensity",
    },
    displacementScale: {
      type: "number",
      inputProps: { step: 0.01 },
      label: "displacementScale",
    },
    displacementBias: {
      type: "number",
      inputProps: { step: 0.01 },
      label: "displacementBias",
    },
    side: {
      type: "number",
      select: true,
      label: 'side',
      children: [
        {
          value: 0,
          name: 'Front'
        },
        {
          value: 1,
          name: 'Back'
        },
        {
          value: 2,
          name: 'Double'
        }
      ]
    },
    blending: {
      type: "number",
      select: true,
      label: 'blending',
      children: [
        {
          value: 0,
          name: "No Blending"
        },
        {
          value: 1,
          name: 'Normal Blending'
        },
        {
          value: 2,
          name: 'Additive Blending'
        },
        {
          value: 3,
          name: 'Subtractive Blending'
        },
        {
          value: 4,
          name: 'Multiply Blending'
        }
      ]
    },
    transparent: {
      label: 'transparent',
      type: 'checkbox',
      select: true,
      children: [
        {
          value: false,
          name: intl.formatMessage({ id: "EditMaterial.false" })
        },
        {
          value: true,
          name: intl.formatMessage({ id: "EditMaterial.true" })
        }
      ]
    },
    alphaTest: {
      label: 'alphaTest',
      type: 'number',
      inputProps: { step: 0.01 }
    }
  };

  function jsonBlob(obj) {
    return new Blob([JSON.stringify(obj)], {
      type: "application/json",
    });
  }

  useEffect(() => {
    let materials = {};
    materialMaps.forEach((m) => {
      mapTypes.forEach((map) => {
        if (m.type === map.type) {
          materials[map.three] = m.url
            ? m.url + '?quality=high'
            : API_URL + fetchPrefix + `/material/${id}/map/${m.id}?quality=high&${versionSearchParam}`;
        }
      });
    });
    setMaterials(materials);
  }, [materialMaps, id]);

  const handleSubmit = (e) => {
    e.preventDefault();

    // check if every asset has a different type
    if (!checkUniqueTypes()) {
      openSnackBar(intl.formatMessage({ id: "EditMaterial.mapWarning" }));
      return;
    }

    const formData = new FormData();

    // filter out newly added maps that have not been added on the server yet
    const materialMapsArr = materialMaps.filter(map => map.id < 0);
    const materialObj = { ...data, localization: getLanguageFields(data) };
    // field 'name' is not needed for server-communication (done through translations)
    delete materialObj.name;
    if (materialObj.object && !materialObj.object.id) {
      materialObj.object = null;
    }

    formData.append("material", jsonBlob({ ...materialObj, maps: materialMapsArr, previewPicture: !previewPicture.id || previewPicture.uploaded ? null : previewPicture }));

    setIsLoading(true);
    if (id === "new") {
      api
        .post(`/material`, formData, {
          headers: {
            Accept: "application/json, text/plain, */*",
            "Content-Type": "multipart/form-data",
          }
        })
        .then((response) => {
          if (onClose) {
            onClose(response.data);
          } else {
            navigate(`/materials${fromTab != null ? `?tab=${fromTab}` : ''}`);
          }
        })
        .catch((error) => {
          // handle errors
          console.log(error);
        })
        .finally(() => {
          setIsLoading(false);
        })
    } else {
      const mapDeletionPromise = mapsToRemove.map(map => {
        return api
          .delete(`/material/${id}/map/${map.id}`)
          .then(() => {
            // removal successful
          })
          .catch((error) => {
            console.log(error);
          });
      });
      let previewPictureDeletionPromise = new Promise(resolve => resolve());
      if (deletePreviewPicture && data.previewPicture) {
        previewPictureDeletionPromise = api
          .delete(`/material/${id}/previewPicture`)
          .catch(error => {
            console.log(error);
          });
      }
      const deletionPromise = Promise.all([...mapDeletionPromise, previewPictureDeletionPromise]);
      deletionPromise
        .then(() => {
          api
            .put(`/material/${id}`, formData, {
              headers: {
                Accept: "application/json, text/plain, */*",
                "Content-Type": "multipart/form-data"
              }
            })
            .then((response) => {
              // handle the response
              if (onClose) {
                onClose(response.data);
              } else {
                navigate(`/materials${fromTab != null ? `?tab=${fromTab}` : ''}`);
              }
            })
            .catch((error) => {
              // handle errors
              console.log(error);
            })
            .finally(() => {
              setIsLoading(false);
            });
        })
    }
  };

  const updateData = useCallback((e) => {
    const fieldName = e.target.name;
    setData((existingValues) => ({
      ...existingValues,
      [fieldName]: e.target.value,
    }));
  }, [setData]);

  const removeMap = (map) => {
    if (map.id >= 0) {
      // map is already uploaded, association must be removed on server
      setMapsToRemove((old) => [...old, map]);
    }
    // remove from materialMaps
    setMaterialMaps((materialMaps) => materialMaps.filter((m) => m.id !== map.id));
  };

  const uploadDone = (d) => {
    if (d === undefined) {
      return;
    }
    setData(d);
    setInitialData({ ...d });
    if (d.previewPicture) {
      setPreviewPicture({ url: API_URL + fetchPrefix + `/material/${id}/previewPicture?quality=high&${versionSearchParam}`, uploaded: true });
    }
    setMaterialMaps(d.maps);
    // reset maps to remove
    setMapsToRemove([]);
  };

  const checkUniqueTypes = () => {
    const usedTypes = {};
    let unique = true;
    materialMaps.forEach(map => {
      if (!map.type || usedTypes[map.type]) {
        unique = false;
      } else {
        usedTypes[map.type] = true;
      }
    });
    return unique;
  };

  const checkUnique = (mapId, mapType) => {
    let unique = true;
    materialMaps.forEach(mm => {
      if (mm.id === mapId) {
        return;
      }
      if (mm.type === mapType) {
        unique = false;
      }
    });
    return unique;
  };

  const handleTypeChange = (mapId, mapType) => {
    if (checkUnique(mapId, mapType)) {
      if (id !== undefined && id !== "new" && mapId !== undefined && mapId >= 0) {
        setIsLoading(true);
        api
          .put(`/material/${id}/map/${mapId}`, { id: mapId, type: mapType })
          .then((response) => {
            // handle the response
            setMaterialMaps(response.data.maps);
          })
          .catch((error) => {
            // handle errors
            console.log(error);
          })
          .finally(() => {
            setIsLoading(false);
          });
      } else {
        setMaterialMaps(oldData => oldData.map(map => {
          if (map.id === mapId) {
            map.type = mapType;
          }
          return map;
        }));
      }
      return true;
    } else {
      openSnackBar(intl.formatMessage({ id: "EditMaterial.mapWarning" }));
      return false;
    }
  };

  useEffect(() => {
    if (id !== "new") {
      setIsLoading(true);
      api
        .get(fetchPrefix + `/material/${id}?${versionSearchParam}&localization=true&lang=${locale}`)
        .then(response => {
          uploadDone(response.data);
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [id, versionSearchParam]);

  const materialMapList = materialMaps.map((m) => {
    return (
      <Grid key={m.id} item xs={12} sm={6} md={3}>
        <MaterialImageCard
          url={m.url ? m.url + '?quality=thumb' : API_URL + fetchPrefix + `/material/${id}/map/${m.id}?quality=thumb&${versionSearchParam}`}
          id={m.id}
          map={m}
          handle={uploadDone}
          removeMap={removeMap}
          handleTypeChange={handleTypeChange}
          disabled={isStatic}
        />
      </Grid>
    );
  });

  /**
   * The normal form-fields, memoized.
   */
  const normalFormFields = useMemo(() => {
    return Object.keys(normalForm).map((key, i) => (
      <Grid item xs={12} sm={6} md={3} key={i}>
        <TextFieldElement properties={normalForm[key]} name={key} initialValue={initialData[key]} updateData={updateData} isStatic={isStatic} />
      </Grid>
    ))
  }, [isStatic, updateData, triggerDataChange, intl]);

  /**
   * The special form-fields, memoized.
   */
  const specialFormFields = useMemo(() => {
    return Object.keys(specialForm).map((key, i) => {
      const offset = Object.keys(normalForm).length + 1;
      return (
        <Grid item xs={12} sm={6} md={3} key={i + offset}>
          <TextFieldElement properties={specialForm[key]} name={key} initialValue={initialData[key]} updateData={updateData} isStatic={isStatic} triggerDataChange={triggerDataChange} />
        </Grid>
      );
    }
    );
  }, [isStatic, updateData, triggerDataChange, intl])

  /**
   * Method to be called when the file-chooser for PreviewPicture has selected data.
   * @param {*} data 
   * @returns 
   */
  const handlePreviewPictureSelect = (data) => () => {
    setPreviewPicture({ ...data, url: `${API_URL}/data/${data.id}/file`, uploaded: false });
    setOpenPreviewPictureChooser(false);
  }

  /**
   * Removes the preview-picture locally from the Material.
   */
  const handlePreviewPictureDelete = () => {
    setPreviewPicture({});
    if (id !== undefined && id !== "new") {
      // should delete preview-picture when data is submitted to server
      setDeletePreviewPicture(true);
    }
  }

  const handleMapSelect = (data) => () => {
    let val = {
      url: `${API_URL}/data/${data.id}/file`,
      image: { ...data },
      id: nextId.current,
    };
    if (materialMaps.length === 0) {
      // make the first selected picture of type 'map', all others must be manually selected
      val.type = 'map'
    }
    nextId.current = nextId.current - 1;
    setMaterialMaps((existingValues) => [...existingValues, val]);
    setOpenMapFileChooser(false);
  }

  const handleChangeDetails = (event) => {
    setShowDetails(event.target.checked);
  }

  const handlePublicChange = (event) => {
    setData(oldData => ({ ...oldData, publicFlag: event.target.checked }));
  };

  /**
   * Trigger change in TextFieldElement.
   */
  useEffect(() => {
    setTriggerDataChange(old => old + 1);
  }, [initialData]);

  /**
   * Resets all special material-values.
   */
  const resetSpecialValues = () => {
    setData(old => ({ ...old, ...defaultMaterialData }));
    setInitialData(old => {
      return { ...old, ...defaultMaterialData }
    });
  }

  /**
   * Triggers the loading screen to become invisible (toVisiblity = true)
   * or visible (toVisibility = false).
   * @param {*} toVisibility 
   * @returns 
   */
  const triggerLoadScreen = (toVisibility) => {
    const loadElement = document.getElementById('loadModelDiv');
    if (!loadElement) return;
    if (toVisibility) {
      loadElement.style.opacity = 0;
      loadElement.style.zIndex = 0;
    } else {
      loadElement.style.opacity = 1;
      loadElement.style.zIndex = 21;
    }
  };

  /**
     * Returns an object with key-value-pairs of translations.
     * If showTranslations = true, the specific translations are used.
     * If showTranslations = false, only uses the current locale.
     * @param {*} data 
     * @returns 
     */
  const getLanguageFields = (data) => {
    if (showTranslations) {
      // translationsRef is used
      if (translationsRef.current) {
        return translationsRef.current.getTranslations();
      } else {
        return {};
      }
    } else {
      // translationsRef is not used
      return { ...data.localization }
    }
  };

  /**
   * Sets the language-field of the current locale.
   * data.name and data.name<Language> is updated.
   * @param {*} value 
   */
  const setLocaleField = (value) => {
    setData(old => {
      const o = { ...old };
      o.localization[locale] = value;
      o.name = value;
      return o;
    });
  };

  return (
    <Container maxWidth='xl' >
      {isLoading && <Box
        sx={{
          marginTop: 3,
          marginBottom: 3,
          position: "fixed",
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: "center",
          alignItems: "center",
          zIndex: 10
        }}
      >
        <CircularProgress color="primary" />
      </Box>}
      <div style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography variant="h4" gutterBottom sx={{ marginTop: 2 }}>
          <FormattedMessage id="EditMaterial.heading" />
        </Typography>
      </div>
      <CustomDialog
        title={intl.formatMessage({ id: "EditMaterial.choosePreviewPicture" })}
        content={<DataTable selectable={true} usageFlags={{ image: true }} onSelect={handlePreviewPictureSelect} />}
        isOpen={openPreviewPictureChooser}
        handleAccept={() => setOpenPreviewPictureChooser(false)}
        handleClose={() => setOpenPreviewPictureChooser(false)}
        maxWidth={"xl"}
        hideConfirmation={true}
      />
      <CustomDialog
        title={intl.formatMessage({ id: "EditMaterial.chooseMaterialMap" })}
        content={<DataTable selectable={true} usageFlags={{ image: true }} onSelect={handleMapSelect} />}
        isOpen={openMapFileChooser}
        handleAccept={() => setOpenMapFileChooser(false)}
        handleClose={() => setOpenMapFileChooser(false)}
        maxWidth={"xl"}
        hideConfirmation={true}
      />
      <Box
        component="form"
        onSubmit={handleSubmit}
      >
        <Grid container spacing={3} alignItems='center' sx={{ marginBottom: 2, width: '100%' }}>
          <Grid item xs={12} md={9}>
            <Box
              sx={{
                height: 500,
                backgroundColor: "grey.300",
                borderRadius: 3
              }}
              style={{
                position: 'relative'
              }}
            >
              <div style={{ width: '100%', height: '100%' }}>
                {/* div for covering up loading-time of material */}
                <div id="loadModelDiv" style={{ position: 'absolute', width: '100%', height: '100%', left: '0px', top: '0px', backgroundColor: 'white', zIndex: 21, borderRadius: 3, transition: 'opacity 0.8s ease-in, z-index 0.8s step-end' }}>
                </div>
                <MaterialPreview mats={materials} data={data} scale={1.0} onLoad={triggerLoadScreen} />
              </div>
            </Box>
          </Grid>
          <Grid container flexDirection='column' justifyContent='center' alignItems='center' item xs={12} md={3}>
            {!isStatic && <Button
              onClick={() => setOpenPreviewPictureChooser(true)}
              variant="outlined"
              sx={{ marginBottom: 1 }}
            >
              <FormattedMessage id="EditMaterial.choosePreviewPicture" />
            </Button>}
            {previewPicture.url &&
              <ImageBox url={previewPicture.url} uploaded={previewPicture.uploaded === true ? true : false} onDelete={handlePreviewPictureDelete} disabled={isStatic} />
            }
          </Grid>
        </Grid>

        <Grid container spacing={3} alignItems='flex-start' sx={{ border: '1px dashed black', borderRadius: 3, marginTop: 3, paddingBottom: 3, marginBottom: 6, width: '100%' }}>
          <Grid item xs={12} >
            <Typography variant="h5" gutterBottom sx={{ margin: 0 }}>
              <FormattedMessage id="EditMaterial.mapHeading" />:
            </Typography>
          </Grid>
          {materialMapList}
          {!isStatic && <Grid
            container
            key={"add"}
            item
            xs={12}
            justifyContent="flex-start"
            alignItems="center"
          >
            <Fab
              color="primary"
              variant="extended"
              aria-label="add"
              onClick={() => setOpenMapFileChooser(true)}
            >
              <AddIcon />
              <FormattedMessage id="EditMaterial.mapHeading" />
            </Fab>
          </Grid>}
        </Grid>

        <Grid container spacing={3} alignItems="center" sx={{ border: '1px dashed black', borderRadius: 3, width: "100%", paddingBottom: 2 }}>
          {
            showTranslations &&
            <TranslationTextFields ref={translationsRef} defaultTranslations={{ ...data.localization }}
              isStatic={isStatic} />
          }
          {
            !showTranslations &&
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                required
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                size="small"
                name={"name"}
                label={intl.formatMessage({ id: "EditMaterial.name" })}
                type="text"
                sx={textFieldStyle}
                value={data.name || ''}
                onChange={(event) => setLocaleField(event.target.value)}
                disabled={isStatic}
                inputProps={{ maxLength: 50 }}
              />
            </Grid>
          }
          {
            <Grid item xs={12} container alignItems={'center'}>
              <CustomToolTip
                tooltip={showTranslations ? <FormattedMessage id='EditObject.hideTranslations' /> : <FormattedMessage id='EditObject.showTranslations' />}
                placement={"bottom"}
              >
                <Button
                  onClick={() => setShowTranslations(old => {
                    if (old) {
                      // update localization if switching from multi-translation-text-fields to single field
                      if (translationsRef.current) {
                        const localization = translationsRef.current.getTranslations();
                        setData(data => ({ ...data, name: localization[locale], localization }));
                      }
                    }
                    return !old;
                  })}
                  aria-label='show translations'
                >
                  <TranslateIcon fontSize='large' />
                </Button>
              </CustomToolTip>
            </Grid>
          }
          {normalFormFields}
          <Grid item xs={12} sm={6} md={4} key={Object.keys(normalForm).length}>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <ComboBox
                label={intl.formatMessage({ id: "EditMaterial.objectType" })}
                selection={data.object}
                onSelect={(mt => {
                  setData(existingValues => ({
                    ...existingValues,
                    object: mt
                  }));
                })}
                required={false}
                url={`/object/all?published=true&all=true&allLanguages=${allLanguages}&lang=${locale}`}
                style={textFieldStyle}
                idTag="object"
                disabled={isStatic}
              />
              {!isStatic && <LanguagesCheckbox initialValue={allLanguages} onChange={setAllLanguages} disabled={isStatic} style={{ marginRight: '10px' }} />}
            </div>

          </Grid>
          {!isStatic && userInfo?.isAdmin && <Grid item xs={12} sm={12} md={12}>
            <FormControlLabel control={<Checkbox checked={data.publicFlag} onChange={handlePublicChange} />} label={intl.formatMessage({ id: "Edit.published" })} />
          </Grid>}
          <Grid item xs={12} >
            <FormControlLabel control={<Switch
              checked={showDetails}
              onChange={handleChangeDetails}
              inputProps={{ 'aria-label': 'controlled' }}
            />}
              label={<p style={{ width: '100px' }}><b>{intl.formatMessage({ id: "Material.profSettingsLabel" })}</b></p>}
              labelPlacement="start" />
          </Grid>
          {showDetails && specialFormFields}
          {!isStatic && showDetails &&
            <Grid item xs={12}>
              <Button startIcon={<RestoreIcon />} variant="outlined" onClick={resetSpecialValues} ><FormattedMessage id="EditMaterial.resetValues" /></Button>
            </Grid>}
        </Grid>
        {!isStatic && <Button sx={{ marginTop: 2, marginBottom: 2 }} variant="contained" type="submit">
          <FormattedMessage id="Edit.save" />
        </Button>}
      </Box>
    </Container>
  );
}
